// React hook helpers
import { useEffect, useState } from 'react';

// Centralized responsive breakpoints aligned with Tailwind defaults
export const BREAKPOINTS = {
  base: 0,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
};

const getBreakpointValue = (key: keyof typeof BREAKPOINTS): number => {
  switch (key) {
    case 'base':
      return BREAKPOINTS.base;
    case 'sm':
      return BREAKPOINTS.sm;
    case 'md':
      return BREAKPOINTS.md;
    case 'lg':
      return BREAKPOINTS.lg;
    case 'xl':
      return BREAKPOINTS.xl;
    case '2xl':
      return BREAKPOINTS['2xl'];
  }
};

export const mediaQueries = {
  up: (key: keyof typeof BREAKPOINTS) => `(min-width: ${getBreakpointValue(key)}px)`,
  down: (key: keyof typeof BREAKPOINTS) => `(max-width: ${getBreakpointValue(key) - 1}px)`,
  between: (minKey: keyof typeof BREAKPOINTS, maxKey: keyof typeof BREAKPOINTS) =>
    `(min-width: ${getBreakpointValue(minKey)}px) and (max-width: ${getBreakpointValue(maxKey) - 1}px)`,
};

export function getCurrentBreakpoint(
  width = typeof window !== 'undefined' ? window.innerWidth : 0
): 'base' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' {
  if (width >= BREAKPOINTS['2xl']) return '2xl';
  if (width >= BREAKPOINTS.xl) return 'xl';
  if (width >= BREAKPOINTS.lg) return 'lg';
  if (width >= BREAKPOINTS.md) return 'md';
  if (width >= BREAKPOINTS.sm) return 'sm';
  return 'base';
}

export function isAtLeast(
  key: keyof typeof BREAKPOINTS,
  width = typeof window !== 'undefined' ? window.innerWidth : 0
): boolean {
  return width >= getBreakpointValue(key);
}

export function isBelow(
  key: keyof typeof BREAKPOINTS,
  width = typeof window !== 'undefined' ? window.innerWidth : 0
): boolean {
  return width < getBreakpointValue(key);
}

export function useMediaQuery(query: string): boolean {
  const getMatches = () => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(query).matches;
  };
  const [matches, setMatches] = useState(getMatches);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', handler);
      return () => mql.removeEventListener('change', handler);
    }
    mql.addListener(handler);
    return () => mql.removeListener(handler);
  }, [query]);
  return matches;
}

export function useIsMobileSm(): boolean {
  return useMediaQuery(mediaQueries.down('sm')); // < 640px
}

export function useIsDesktopLgUp(): boolean {
  return useMediaQuery(mediaQueries.up('lg')); // >= 1024px
}
